import { useEffect, useRef } from "react";
import { chatService } from "../lib/chatService";

/**
 * Custom hook to manage chat subscription cleanup
 * Ensures all subscriptions are properly cleaned up when components unmount
 * or when the user navigates away from chat-related pages
 */
export const useChatCleanup = () => {
  const cleanupExecutedRef = useRef(false);

  // Cleanup function
  const performCleanup = () => {
    if (!cleanupExecutedRef.current) {
      console.log("🧹 Performing global chat cleanup...");

      try {
        // Clear all caches and subscriptions from ChatService
        chatService.cleanup();

        // Mark cleanup as executed
        cleanupExecutedRef.current = true;

        console.log("✅ Global chat cleanup completed successfully");
      } catch (error) {
        console.error("❌ Error during global chat cleanup:", error);
      }
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      performCleanup();
    };
  }, []);

  // Cleanup when page visibility changes (user switches tabs/apps)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log("📱 Page hidden - performing chat cleanup for performance");
        performCleanup();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Cleanup on beforeunload (user navigating away or closing tab)
  useEffect(() => {
    const handleBeforeUnload = () => {
      console.log("🚪 User navigating away - performing chat cleanup");
      performCleanup();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Manual cleanup function (can be called by components if needed)
  const manualCleanup = () => {
    cleanupExecutedRef.current = false; // Reset flag
    performCleanup();
  };

  // Get current subscription count (for debugging)
  const getActiveSubscriptions = () => {
    try {
      return chatService.getActiveSubscriptionCount();
    } catch (error) {
      console.error("❌ Error getting subscription count:", error);
      return 0;
    }
  };

  return {
    manualCleanup,
    getActiveSubscriptions,
  };
};

/**
 * Hook specifically for chat conversation cleanup
 * Use this in components that manage individual conversations
 */
export const useConversationCleanup = (conversationId) => {
  useEffect(() => {
    return () => {
      if (conversationId) {
        console.log(
          `🧹 Cleaning up conversation subscriptions: ${conversationId}`
        );

        try {
          // Unsubscribe from specific conversation
          chatService.unsubscribeFromMessages(conversationId);
          chatService.unsubscribeFromReactions(conversationId);

          // Clear conversation-specific caches
          chatService.clearConversationCaches(conversationId);

          console.log(`✅ Conversation cleanup completed: ${conversationId}`);
        } catch (error) {
          console.error(
            `❌ Error cleaning up conversation ${conversationId}:`,
            error
          );
        }
      }
    };
  }, [conversationId]);
};

/**
 * Hook specifically for media buyer dashboard cleanup
 * Use this in the MediaBuyerChatDashboard component
 */
export const useDashboardCleanup = (mediaBuyerId) => {
  useEffect(() => {
    return () => {
      if (mediaBuyerId) {
        console.log(`🧹 Cleaning up dashboard subscriptions: ${mediaBuyerId}`);

        try {
          // Unsubscribe from dashboard updates
          chatService.unsubscribeFromDashboard(mediaBuyerId);

          // Clear dashboard-specific caches
          chatService.clearDashboardCache(mediaBuyerId);

          console.log(`✅ Dashboard cleanup completed: ${mediaBuyerId}`);
        } catch (error) {
          console.error(
            `❌ Error cleaning up dashboard ${mediaBuyerId}:`,
            error
          );
        }
      }
    };
  }, [mediaBuyerId]);
};
